import { useEffect, useState } from "react"

import axios from "axios"
import { useTranslation } from "react-i18next"

import { AlertTriangle } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Spinner } from "@/components/ui/spinner"

type DeleteHostedAgentDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void>
  isPending: boolean
  agent: { id: string; name: string; slug: string } | null
}

export function DeleteHostedAgentDialog({
  isOpen,
  onOpenChange,
  onConfirm,
  isPending,
  agent,
}: DeleteHostedAgentDialogProps) {
  const { t } = useTranslation()
  const [decommissionRequired, setDecommissionRequired] = useState(false)
  useEffect(() => {
    setDecommissionRequired(false)
  }, [isOpen, agent?.id])

  if (!agent) return null

  const handleConfirm = async () => {
    try {
      await onConfirm()
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response.data?.code === "managed_runtime_requires_decommission"
      )
        setDecommissionRequired(true)
      // The owner already toasted the error — keep the dialog open for retry.
      return
    }
    onOpenChange(false)
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-foreground size-5" />
            {t("adminDashboard.hostedAgents.deleteDialog.title", {
              defaultValue: "Delete hosted agent",
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("adminDashboard.hostedAgents.deleteDialog.description", {
              defaultValue:
                'Are you sure you want to delete "{{name}}" ({{slug}})? Users lose access immediately and this action cannot be undone.',
              name: agent.name,
              slug: agent.slug,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {decommissionRequired ? (
          <p className="text-sm" role="alert">
            {t("adminDashboard.hostedAgents.deleteDialog.decommissionRequired", {
              defaultValue:
                "Open the agent's Lifecycle tab and remove its running environment first. Wait until removal succeeds, then retry deleting the registration. Reports and history are retained.",
            })}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t("adminDashboard.hostedAgents.deleteDialog.cancel", { defaultValue: "Cancel" })}
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isPending}
            onClick={(e) => {
              e.preventDefault()
              void handleConfirm()
            }}
          >
            {isPending ? (
              <>
                <Spinner />
                {t("adminDashboard.hostedAgents.deleteDialog.deleting", {
                  defaultValue: "Deleting...",
                })}
              </>
            ) : (
              t("adminDashboard.hostedAgents.deleteDialog.delete", { defaultValue: "Delete agent" })
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
